import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { Plus, Edit, Eye, Users, Clock, CheckCircle, AlertCircle, Play } from 'lucide-react';

const AdminQuizzes: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'my-quizzes' | 'pending-reviews'>('my-quizzes');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState<number | null>(null);

  // Mock data - replace with actual API calls
  const myQuizzes = [
    {
      id: 1,
      title: 'Évaluation GPS - Module 3',
      course: 'Expert gestion GPS',
      questions: 15,
      timeLimit: 30,
      attempts: 342,
      averageScore: 78,
      passingScore: 70,
      difficulty: 'Intermédiaire',
      isActive: true,
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      title: 'Quiz Charge d\'entraînement - Final',
      course: 'Expert charge d\'entraînement',
      questions: 25,
      timeLimit: 45,
      attempts: 128,
      averageScore: 82,
      passingScore: 75,
      difficulty: 'Avancé',
      isActive: true,
      createdAt: '2024-01-14T14:20:00Z'
    },
    {
      id: 3,
      title: 'Test Réathlétisation - Partie 1',
      course: 'Expert réathlétisation',
      questions: 12,
      timeLimit: 20,
      attempts: 67,
      averageScore: 85,
      passingScore: 70,
      difficulty: 'Débutant',
      isActive: false,
      createdAt: '2024-01-13T09:15:00Z'
    }
  ];

  const pendingReviews = [
    {
      id: 1,
      student: 'Sophie Bernard',
      studentEmail: 'sophie.bernard@email.com',
      quiz: 'Quiz Charge d\'entraînement - Final',
      course: 'Expert charge d\'entraînement',
      submittedAt: '2024-01-15T16:30:00Z',
      answers: 25,
      autoScore: 18,
      needsManualReview: true,
      shortAnswers: 3
    },
    {
      id: 2,
      student: 'Lucas Petit',
      studentEmail: 'lucas.petit@email.com',
      quiz: 'Évaluation GPS - Module 3',
      course: 'Expert gestion GPS',
      submittedAt: '2024-01-15T14:15:00Z',
      answers: 15,
      autoScore: 12,
      needsManualReview: true,
      shortAnswers: 2
    },
    {
      id: 3,
      student: 'Marie Durand',
      studentEmail: 'marie.durand@email.com',
      quiz: 'Test Réathlétisation - Partie 1',
      course: 'Expert réathlétisation',
      submittedAt: '2024-01-14T11:45:00Z',
      answers: 12,
      autoScore: 10,
      needsManualReview: true,
      shortAnswers: 1
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Débutant': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'Intermédiaire': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'Avancé': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score: number, passingScore: number) => {
    if (score >= passingScore) return 'text-[#FFB639]';
    if (score >= passingScore * 0.8) return 'text-[#FFB639]';
    return 'text-red-600';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Quiz & Évaluations</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un quiz
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('my-quizzes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-quizzes'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mes Quiz ({myQuizzes.length})
            </button>
            <button
              onClick={() => setActiveTab('pending-reviews')}
              className={`py-2 px-1 border-b-2 font-medium text-sm relative ${
                activeTab === 'pending-reviews'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              À corriger ({pendingReviews.length})
              {pendingReviews.length > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              )}
            </button>
          </nav>
        </div>

        {/* My Quizzes Tab */}
        {activeTab === 'my-quizzes' && (
          <div className="space-y-4">
            {myQuizzes.map((quiz) => (
              <div key={quiz.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(quiz.difficulty)}`}>
                        {quiz.difficulty}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        quiz.isActive ? 'bg-[#FFB639]/15 text-[#FFB639]' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {quiz.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{quiz.course}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center text-gray-500">
                        <Users className="h-4 w-4 mr-1" />
                        {quiz.attempts} tentatives
                      </div>
                      <div className="flex items-center text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {quiz.timeLimit} minutes
                      </div>
                      <div className="flex items-center text-gray-500">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        {quiz.questions} questions
                      </div>
                      <div className={`flex items-center ${getScoreColor(quiz.averageScore, quiz.passingScore)}`}>
                        <span className="font-medium">
                          Moy: {quiz.averageScore}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setSelectedQuiz(quiz.id)}
                      className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                      title="Voir les résultats"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                      title="Prévisualiser"
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pending Reviews Tab */}
        {activeTab === 'pending-reviews' && (
          <div className="space-y-4">
            {pendingReviews.map((review) => (
              <div key={review.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{review.student}</h3>
                      <span className="px-2 py-1 text-xs font-medium bg-[#FFB639]/15 text-[#FFB639] rounded-full flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        En attente
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1">{review.quiz}</p>
                    <p className="text-xs text-gray-500 mb-4">{review.course}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center text-gray-500">
                        <span>Soumis le:</span>
                        <span className="ml-1 font-medium">
                          {new Date(review.submittedAt).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500">
                        <span>Score auto:</span>
                        <span className="ml-1 font-medium">
                          {review.autoScore}/{review.answers}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500">
                        <span>Questions ouvertes:</span>
                        <span className="ml-1 font-medium text-[#FFB639]">
                          {review.shortAnswers}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500">
                        <span>Email:</span>
                        <span className="ml-1 text-[#FFB639] text-xs">
                          {review.studentEmail}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4">
                    <button
                      onClick={() => alert(`Correction de ${review.student} (À implémenter)`)}
                      className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                    >
                      Corriger
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {pendingReviews.length === 0 && (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-[#FFB639] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune correction en attente
                </h3>
                <p className="text-gray-500">
                  Toutes les soumissions ont été corrigées.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Quiz Results Modal */}
        {selectedQuiz && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {(() => {
                const quiz = myQuizzes.find(q => q.id === selectedQuiz);
                if (!quiz) return null;

                return (
                  <>
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Résultats: {quiz.title}
                          </h2>
                          <p className="text-gray-600">{quiz.course}</p>
                        </div>
                        <button
                          onClick={() => setSelectedQuiz(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-[#FFB639]/10 rounded-lg p-4">
                          <div className="text-2xl font-bold text-[#FFB639]">{quiz.attempts}</div>
                          <div className="text-sm text-[#FFB639]">Tentatives totales</div>
                        </div>
                        <div className="bg-[#FFB639]/10 rounded-lg p-4">
                          <div className="text-2xl font-bold text-[#FFB639]">{quiz.averageScore}%</div>
                          <div className="text-sm text-[#FFB639]">Score moyen</div>
                        </div>
                        <div className="bg-[#FFB639]/10 rounded-lg p-4">
                          <div className="text-2xl font-bold text-[#FFB639]">
                            {Math.round((quiz.averageScore / 100) * quiz.attempts)}
                          </div>
                          <div className="text-sm text-[#FFB639]">Étudiants réussis</div>
                        </div>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Tentatives récentes
                      </h3>
                      
                      <div className="space-y-3">
                        {[...Array(10)].map((_, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <h4 className="font-medium text-gray-900">
                                Étudiant {index + 1}
                              </h4>
                              <p className="text-sm text-gray-500">
                                Terminé le {new Date(Date.now() - index * 86400000).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div className="text-right">
                              <div className={`text-lg font-bold ${
                                (85 - index * 3) >= quiz.passingScore ? 'text-[#FFB639]' : 'text-red-600'
                              }`}>
                                {85 - index * 3}%
                              </div>
                              <p className="text-sm text-gray-500">
                                {Math.round((85 - index * 3) / 100 * quiz.questions)}/{quiz.questions}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                      <button
                        onClick={() => alert('Export des résultats (À implémenter)')}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Exporter les résultats
                      </button>
                      <button
                        className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                      >
                        Modifier le quiz
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Create Quiz Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">Créer un nouveau quiz</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre du quiz
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                        placeholder="Ex: Évaluation GPS - Module 1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cours associé
                      </label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]">
                        <option value="">Sélectionner un cours</option>
                        <option value="1">Expert gestion GPS</option>
                        <option value="2">Expert charge d'entraînement</option>
                        <option value="3">Expert réathlétisation</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                      placeholder="Décrivez les objectifs et le contenu de ce quiz..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Temps limite (minutes)
                      </label>
                      <input
                        type="number"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                        placeholder="30"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tentatives max
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                        placeholder="3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Score de passage (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                        placeholder="70"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulté
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]">
                      <option value="beginner">Débutant</option>
                      <option value="intermediate">Intermédiaire</option>
                      <option value="advanced">Avancé</option>
                    </select>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Questions</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 text-center">
                          Aucune question ajoutée. Cliquez sur "Ajouter une question" pour commencer.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#FFB639] hover:text-[#FFB639] transition-colors"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        Ajouter une question
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    alert('Quiz créé ! (Fonctionnalité à implémenter)');
                    setShowCreateModal(false);
                  }}
                  className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                >
                  Créer le quiz
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminQuizzes;